import React, { useState } from 'react';
import Snackbar from '../../../utils/Snackbar/Snackbar';
import AlertMsg from '../../../component/AlertMsg/AlertMsg';
import useAxiosInstance from '../../../services/useAxiosInstance';

const EditCommunityDetails = (props) => {
    const axiosInstance = useAxiosInstance();
    const [communityData, setCommunityData] = useState(props.initialData);
    const [successMessage, setSuccessMessage] = useState('');
    const [isSnackbarOpen, setIsSnackbarOpen] = useState({ open: false, type: '' });
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
    const [errors, setErrors] = useState({});

    const handleChange = (e, fieldName) => {
        const { value } = e.target;
        setCommunityData(prevData => ({
            ...prevData,
            [fieldName]: value
        }));

        // Clear error for the field being changed
        if (errors[fieldName]) {
            setErrors({
                ...errors,
                [fieldName]: undefined
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!communityData.name || communityData.name.trim() === '') {
            newErrors.name = 'Community name is required';
        }
        if (!communityData.contactperson || communityData.contactperson.trim() === '') {
            newErrors.contactperson = 'Contact person is required';
        }
        if (!communityData.totalowners) {
            newErrors.totalowners = 'Total number of owners is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const updatedData = {
            ...props.initialData,
            name: communityData.name,
            contactperson: communityData.contactperson,
            totalowners: communityData.totalowners
        };

        try {
            const response = await axiosInstance.put(
                `/api/public/community/update/84`,
                updatedData
            );

            setAlert({ visible: true, message: 'Community details updated successfully!', type: 'success' });
            props.onSubmit(updatedData);
        } catch (error) {
            console.error('Error updating community details:', error);
            setAlert({ visible: true, message: 'Error updating community details: Please try again.', type: 'error' });
        }
    };

    const handleCloseSnackbar = () => {
        setIsSnackbarOpen({ open: false, type: '' });
    };

    const handleCancel = () => {
        // Reset form or navigate back as needed
        setCommunityData(props.initialData);
        setErrors({});
    };

    return (
        <div className="bg-gray-50 min-h-screen md:p-4">
            <div className="max-w-7xl mx-auto">
                {alert.visible && (
                    <AlertMsg
                        message={alert.message}
                        type={alert.type}
                        onClose={() => setAlert({ ...alert, visible: false })}
                    />
                )}

                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">Edit Community Details</h1>
                </div>

                <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                    <form className="p-4" onSubmit={handleSubmit}>
                        <div className="flex flex-wrap gap-6 mb-6">
                            <div className="w-full md:w-[calc(50%-1.5rem)]">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name of the community <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={communityData.name || ''}
                                    onChange={(e) => handleChange(e, 'name')}
                                    className={`w-full rounded-md border ${errors.name ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                />
                                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                            </div>

                            <div className="w-full md:w-[calc(50%-1.5rem)]">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={communityData.contactperson || ''}
                                    onChange={(e) => handleChange(e, 'contactperson')}
                                    className={`w-full rounded-md border ${errors.contactperson ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                />
                                {errors.contactperson && <p className="text-red-500 text-xs mt-1">{errors.contactperson}</p>}
                            </div>

                            <div className="w-full md:w-[calc(50%-1.5rem)]">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Total Number of Home Owners <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={communityData.totalowners || ''}
                                    onChange={(e) => handleChange(e, 'totalowners')}
                                    className={`w-full rounded-md border ${errors.totalowners ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                />
                                {errors.totalowners && <p className="text-red-500 text-xs mt-1">{errors.totalowners}</p>}
                            </div>
                        </div>

                        <div className="flex justify-between items-center">
                            <div className="text-sm text-red-500">* indicates required field</div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <Snackbar
                message={successMessage}
                open={isSnackbarOpen.open}
                onClose={handleCloseSnackbar}
                type={isSnackbarOpen.type}
            />
        </div>
    );
};

export default EditCommunityDetails;